import { useGetValidatorsApy } from '@mysten/core';
import { useSuiClientQuery } from '@mysten/dapp-kit';
import type { SuiSystemStateSummary, SuiValidatorSummary } from '@mysten/sui.js/client';
import { useMemo } from 'react';

import { mistToBigInt } from '~/utils/explorerFormat';

export type CpcValidatorApyEntry = {
	apy: number;
	isApyApproxZero: boolean;
	/** 链上 suix_getValidatorsApy 无样本时，按补贴参数估算 */
	isEstimated?: boolean;
};

function readSubsidyMist(systemState: SuiSystemStateSummary): bigint {
	const nested = (
		systemState as SuiSystemStateSummary & {
			stakeSubsidy?: { currentDistributionAmount?: string };
		}
	).stakeSubsidy?.currentDistributionAmount;
	const flat = (
		systemState as SuiSystemStateSummary & {
			stakeSubsidyCurrentDistributionAmount?: string;
		}
	).stakeSubsidyCurrentDistributionAmount;
	return mistToBigInt(nested ?? flat);
}

/** 按全网补贴与 epoch 时长估算年化收益率（百分数，如 5.2 = 5.2%） */
export function estimateNetworkAnnualApyPercent(
	validators: SuiValidatorSummary[],
	systemState: SuiSystemStateSummary | undefined | null,
): number | null {
	if (!systemState || validators.length === 0) return null;

	const subsidyMist = readSubsidyMist(systemState);
	const epochDurationMs = Number(systemState.epochDurationMs ?? 0);
	if (subsidyMist <= 0n || !Number.isFinite(epochDurationMs) || epochDurationMs <= 0) {
		return null;
	}

	const totalStakeMist = validators.reduce(
		(acc, v) => acc + mistToBigInt(v.stakingPoolSuiBalance),
		0n,
	);
	if (totalStakeMist <= 0n) return null;

	const epochReturn = Number(subsidyMist) / Number(totalStakeMist);
	const epochsPerDay = 86_400_000 / epochDurationMs;
	const dailyReturn = epochReturn * epochsPerDay;
	const annualPercent = dailyReturn * 365 * 100;
	if (!Number.isFinite(annualPercent) || annualPercent <= 0) return null;
	return annualPercent;
}

function buildApyMap(
	validators: SuiValidatorSummary[],
	validatorsApy: Record<string, CpcValidatorApyEntry> | undefined,
	networkAnnualPercent: number | null,
): Record<string, CpcValidatorApyEntry> {
	const map: Record<string, CpcValidatorApyEntry> = {};

	for (const v of validators) {
		const rpc = validatorsApy?.[v.suiAddress];
		const rpcValid = rpc && !rpc.isApyApproxZero && rpc.apy > 0;

		if (rpcValid) {
			map[v.suiAddress] = rpc;
			continue;
		}

		if (networkAnnualPercent != null) {
			const commission = Number(v.commissionRate) / 10_000;
			map[v.suiAddress] = {
				apy: Math.max(0, networkAnnualPercent * (1 - commission)),
				isApyApproxZero: false,
				isEstimated: true,
			};
			continue;
		}

		map[v.suiAddress] = rpc ?? { apy: 0, isApyApproxZero: true };
	}

	return map;
}

export function averageApyPercent(map: Record<string, CpcValidatorApyEntry>): number | null {
	const vals = Object.values(map).filter((v) => v.apy > 0 && !v.isApyApproxZero);
	if (!vals.length) return null;
	return vals.reduce((s, v) => s + v.apy, 0) / vals.length;
}

/** CPC：合并链上 APY；无样本时用补贴参数估算（本地链 epoch 短时常为 null） */
export function useCpcValidatorsApy(validators: SuiValidatorSummary[]) {
	const { data: validatorsApy, isPending: apyPending } = useGetValidatorsApy();
	const { data: systemState, isPending: systemPending } = useSuiClientQuery(
		'getLatestSuiSystemState',
	);

	const networkAnnualPercent = useMemo(
		() => estimateNetworkAnnualApyPercent(validators, systemState),
		[validators, systemState],
	);

	const apyMap = useMemo(
		() => buildApyMap(validators, validatorsApy, networkAnnualPercent),
		[validators, validatorsApy, networkAnnualPercent],
	);

	const avgApy = useMemo(() => averageApyPercent(apyMap), [apyMap]);

	return {
		apyMap,
		avgApy,
		isPending: apyPending || systemPending,
		isUsingEstimates: Object.values(apyMap).some((e) => e.isEstimated),
	};
}
